import { useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ReportCard from "./reportCard";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

// Each stop on the route is a report group. Order here is the order they
// appear on the page, numbered like stops along a line.
const REPORT_GROUPS = [
  {
    key: "fleet",
    label: "Fleet & Vehicle",
    stop: "01",
    reports: [
      // { icon: "airport_shuttle", title: "Vehicle Report", path: "/Reports/Vehicle-reports" },
      { icon: "flash_on", title: "Ignition Report", path: "/Reports/Ignition-reports" },
      {
        icon: "electric_car",
        title: "EV Report",
        path: "/Reports/EV-reports",
      },
      // { icon: "directions_car", title: "Vehicle Performance Report", path: "/Reports/Vehicle-Performance-reports" },
    ],
  },
  {
    key: "location",
    label: "Location & Geofencing",
    stop: "02",
    reports: [
      // { icon: "pin_drop", title: "Geofence Report", path: "/Reports/Geofence-reports" },
      { icon: "pin_drop", title: "Geofence Trip Report", path: "/Reports/Geofence-trip-reports" },
      // { icon: "gps_fixed", title: "Tracking Report", path: "/Reports/Tracking-reports" },
      // { icon: "layers", title: "Catchment Map", path: "/Reports/Cachement-Dashboard" },
    ],
  },
  {
    key: "trips",
    label: "Trips, Stops & Routes",
    stop: "03",
    reports: [
      // { icon: "route", title: "Journey Report", path: "/Reports/Journey-reports" },
      // { icon: "timer", title: "Stops Timing Report", path: "/Reports/Stop-timing-reports" },
      // { icon: "straighten", title: "Stop Distance Report", path: "/Reports/Stop-distance-report" },
      // { icon: "alt_route", title: "Route Deviation Report", path: "/Reports/Route-deviation-reports" },
      { icon: "pause_circle", title: "Halt Report", path: "/Reports/Halt-reports" },
    ],
  },
  {
    key: "distance",
    label: "Distance & Attendance",
    stop: "04",
    reports: [
      // { icon: "map", title: "Distance Report", path: "/Reports/Distance-reports" },
      // { icon: "map", title: "Odometer Distance Report", path: "/Reports/Odometer-distance-reports", flag: "isOdometer" },
      { icon: "today", title: "Daywise Distance Report", path: "/Reports/Daywise-distance-reports" },
      // { icon: "assignment_ind", title: "Attendance Report", path: "/Reports/Attendance-Reports", flag: "isAttendance" },
    ],
  },
];

function AllReports() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const { isAttendance, isOdometer, isLiveCamera } = useSelector((state) => state.account);

  const flags = { isAttendance, isOdometer, isLiveCamera };

  const visibleGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return REPORT_GROUPS.map((group) => {
      const reports = group.reports
        .filter((r) => (r.flag ? flags[r.flag] : true))
        .filter((r) => !q || r.title.toLowerCase().includes(q));
      return { ...group, reports };
    }).filter((group) => group.reports.length > 0);
  }, [query, isAttendance, isOdometer, isLiveCamera]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox mt={2}>
        {/* Header row */}
        <MDBox
          display="flex"
          flexWrap="wrap"
          alignItems="flex-end"
          justifyContent="space-between"
          gap={2}
          mb={1}
        >
          {/* <MDBox>
            <MDTypography
              variant="h4"
              fontWeight="bold"
              sx={{ fontFamily: '"Space Grotesk", "Roboto", sans-serif', color: "#1B2A4A" }}
            >
              All Reports
            </MDTypography>
          </MDBox> */}

          <TextField
            size="small"
            placeholder="Search reports…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{
              minWidth: 260,
              background: "#fff",
              borderRadius: "10px",
              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon fontSize="small" sx={{ color: "#94A3B8" }}>
                    search
                  </Icon>
                </InputAdornment>
              ),
            }}
          />
        </MDBox>

        {/* Signature element: a dashed route line running under the header,
            each dot marking a stop — echoing the bus-route data this
            dashboard is built around. */}
        <MDBox
          sx={{
            height: "2px",
            width: "100%",
            mb: 2,
            backgroundImage:
              "repeating-linear-gradient(to right, #C9CEDA 0, #C9CEDA 6px, transparent 6px, transparent 12px)",
          }}
        />

        {visibleGroups.length === 0 && (
          <MDBox py={6} textAlign="center">
            <MDTypography variant="body2" sx={{ color: "#94A3B8" }}>
              No reports match “{query}”. Try a different search.
            </MDTypography>
          </MDBox>
        )}

        {visibleGroups.map((group) => (
          <MDBox key={group.key} mb={2.5}>
            <MDBox display="flex" alignItems="center" gap={1} mb={1}>
              <MDBox
                sx={{
                  display: "grid",
                  placeItems: "center",
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  border: "1.5px solid #1B2A4A",
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: "0.6rem",
                  fontWeight: 600,
                  color: "#1B2A4A",
                }}
              >
                {group.stop}
              </MDBox>
              <MDTypography
                variant="button"
                fontWeight="bold"
                sx={{
                  fontFamily: '"Space Grotesk", "Roboto", sans-serif',
                  color: "#1B2A4A",
                  fontSize: "0.85rem",
                  letterSpacing: "0.02em",
                }}
              >
                {group.label}
              </MDTypography>
            </MDBox>

            <Grid container spacing={1.25}>
              {group.reports.map((report) => (
                <Grid item xs={4} sm={3} md={2} lg={1.5} xl={1.2} key={report.path}>
                  <MDBox height="100%" sx={{ "& > div": { height: "104px" } }}>
                    <ReportCard
                      icon={report.icon}
                      title={report.title}
                      category={group.key}
                      onClick={() => navigate(report.path)}
                    />
                  </MDBox>
                </Grid>
              ))}
            </Grid>
          </MDBox>
        ))}
      </MDBox>
    </DashboardLayout>
  );
}

export default AllReports;